import { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, LucideSparkles, LucideLoader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BACKEND_URL } from "@/constants/api";
import { useAuth } from "@clerk/clerk-react";
import { buildUserPrompt } from "./buildUserPrompt";

/**
 * Modal that lets teachers review and adjust AI generation parameters before
 * submitting. Shows an editable system prompt, read-only report info, the
 * existing comment, and a free-text instructions field.
 *
 * Props:
 *   open                - controlled open state
 *   onClose             - called when the modal should close
 *   defaultSystemPrompt - initial value for the system prompt textarea (from config)
 *   studentReportInfo   - object of key/value pairs displayed read-only
 *   currentComment      - current textarea value; shown read-only as refinement context
 *   reportId            - passed to the backend
 *   onCommentGenerated  - called with the generated comment string on success
 */
export function AICommentModal({
  open,
  onClose,
  defaultSystemPrompt,
  studentReportInfo,
  currentComment,
  reportId,
  onCommentGenerated,
}) {
  const { getToken } = useAuth();
  const [systemPrompt, setSystemPrompt] = useState(defaultSystemPrompt || "");
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Reset editable state each time the modal opens
  useEffect(() => {
    if (open) {
      setSystemPrompt(defaultSystemPrompt || "");
      setAdditionalInstructions("");
      setError(null);
    }
  }, [open, defaultSystemPrompt]);

  const handleGenerate = async () => {
    setError(null);
    const token = await getToken();
    if (!token) {
      setError("Sign in to use AI comment generation.");
      return;
    }
    setIsGenerating(true);
    try {
      const userPrompt = buildUserPrompt(currentComment, additionalInstructions);
      const res = await fetch(`${BACKEND_URL}api/report-comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reportId: reportId || "free-trial",
          systemPrompt,
          userPrompt,
          studentReportInfo: studentReportInfo || {},
        }),
      });
      if (res.status === 403) {
        const body = await res.json();
        setError(body.message || "AI generation quota exceeded. Please upgrade your plan.");
        return;
      }
      if (!res.ok) {
        setError("Failed to generate AI comment. Please try again.");
        return;
      }
      const { comment } = await res.json();
      onCommentGenerated(comment);
    } catch {
      setError("Failed to generate AI comment. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const infoEntries = Object.entries(studentReportInfo || {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">

          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-slate-900">Generate with AI</Dialog.Title>
            <Dialog.Close asChild>
              <button className="rounded-sm opacity-60 hover:opacity-100 transition-opacity focus:outline-none">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-5">
            {/* System Prompt */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block">
                System Prompt
              </label>
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                className="min-h-[100px] resize-none text-sm font-mono"
              />
              <p className="text-xs text-slate-400">
                Defines the AI&apos;s role and writing style. Edit to customize the output.
              </p>
            </div>

            {/* Report Info */}
            {infoEntries.length > 0 && (
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block">
                  Report Info
                </label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                    {infoEntries.map(([key, value]) => (
                      <div key={key} className="flex gap-1.5 min-w-0">
                        <dt className="text-[11px] text-slate-400 shrink-0">{formatKey(key)}:</dt>
                        <dd className="text-[11px] text-slate-700 font-medium truncate">{String(value)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <p className="text-xs text-slate-400">This data is appended to the request automatically.</p>
              </div>
            )}

            {/* Existing Comment */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block">
                Existing Comment
              </label>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 min-h-[60px] whitespace-pre-wrap">
                {currentComment?.trim() ? (
                  currentComment
                ) : (
                  <span className="italic text-slate-400 text-xs">No existing comment &mdash; AI will generate from scratch.</span>
                )}
              </div>
              <p className="text-xs text-slate-400">
                If present, the AI will refine this comment. Otherwise it generates fresh.
              </p>
            </div>

            {/* Additional Instructions */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block">
                Additional Instructions
              </label>
              <Textarea
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                placeholder="e.g. Focus on reading progress. Keep it to 2 sentences."
                className="min-h-[80px] resize-none text-sm"
              />
            </div>

            {/* Error */}
            {error && <p className="text-sm text-red-600">{error}</p>}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={isGenerating}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleGenerate}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <LucideLoader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <LucideSparkles size={14} className="mr-1.5" />
                )}
                Generate
              </Button>
            </div>
          </div>

        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Converts a camelCase key into a readable label: "daysAbsent" → "Days Absent" */
function formatKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}
